import React from "react";

import { MenuHorizontal } from "icons";
import * as yup from "yup";

import { Tag } from "clearsense-ui/components";
import { Steward } from "components/Common/TableStyle";

const getStatusColor = text => {
  switch (text) {
    case "success":
      return "#EBF4EC";
    case "In Progress":
      return "#E8F4FD";
    case "failed":
      return "#FAEAEA";
    default:
      return "";
  }
};

export const COLUMNS = [
  "Conversion Name",
  "Description",
  "Converter",
  "Status",
  "Steward",
  "Action",
];

export const FHIR_CONVERTER_COLUMN = [
  {
    dataIndex: "name",
    key: "conversion_name",
    title: "Conversion Name",
    width: 200,
  },
  {
    dataIndex: "description",
    key: "description",
    title: "Description",
    width: 200,
  },
  {
    dataIndex: "conversion_type",
    key: "converter",
    title: "Converter",
    width: 150,
  },
  {
    dataIndex: "status",
    key: "status",
    title: "Status",
    width: 150,
    render: text =>
      text ? (
        <Tag
          label={text}
          color={getStatusColor(text)}
          className="flex-row-reverse "
        />
      ) : (
        <Tag
          label="Good"
          color={getStatusColor("Good")}
          className="flex-row-reverse "
        />
      ),
  },
  {
    dataIndex: "steward",
    key: "steward",
    title: "Steward",
    width: 150,
    render: text => <Steward name={text?.name} title={text?.role} />,
  },
  {
    key: "action",
    title: "Action",
    width: 100,
    render: () => <MenuHorizontal />,
  },
];

export const CONVERSIONS = [
  {
    id: 1,
    conversion_name: "Payer",
    description: "Payer",
    converter: "HL7v2",
    status: "Success",
    steward: { name: "Tom Cook", role: "Data Steward" },
  },
  {
    id: 2,
    conversion_name: "Provider",
    description: "Provider",
    converter: "HL7v2",
    status: "Success",
    steward: { name: "Tom Cook", role: "Data Steward" },
  },
  {
    id: 3,
    conversion_name: "NPI from CMS claims",
    description: "NPI from CMS claims",
    converter: "C-CDA",
    status: "Failed",
    steward: { name: "Tom Cook", role: "Data Steward" },
  },
  {
    id: 4,
    conversion_name: "Claims bulk import",
    description: "Bulk import of FHIR compliant data.",
    converter: "FHIR",
    status: "In Progress",
    steward: { name: "Tom Cook", role: "Data Steward" },
  },
];

export const CONVERSION_TYPE = [
  { value: "Hl7v2", label: "HL7v2 to FHIR" },
  { value: "Ccda", label: "C-CDA to FHIR" },
  { value: "Json", label: "JSON to FHIR" },
];

export const INITIAL_FHIR_CONVERSION_VALUES = {
  conversionName: "",
  description: "",
  conversionType: "",
  drafter: "",
  approver: "",
};

export const FHIR_CONVERSION_VALIDATION = yup.object().shape({
  conversionName: yup.string().required("Conversion name is a required field."),
  description: yup.string().required("Description is a required field."),
  conversionType: yup.object().required("Conversion type is a required field."),
  drafter: yup.object().required("Drafter is a required field."),
  approver: yup.object().required("Approver is a required field."),
});

export const CONVERSION_DATA_VALIDATION = yup.object().shape({
  conversionType: yup.object().required("Conversion type is a required field."),
  conversionData: yup.string().required("Data is a required field."),
});

export const FORMAT_REFERENCE = {
  Hl7v2:
    "MSH|^~\\&|GHH LAB|ELAB-3|GHH OE|BLDG4|200202150930||ORU^R01|CNTRL-3456|P|2.4\nPID|||555-44-4444||EVERYWOMAN^EVE^E^^^^L|JONES|19620320|F|||153 FERNWOOD DR.^^STATESVILLE^OH^35292||(206)3345232|(206)752-121||||AC555444444||67-A4335^OH^20030520\nOBR|1|845439^GHH OE|1045813^GHH LAB|15545^GLUCOSE|||200202150730|||||||||555-55-5555^PRIMARY^PATRICIA P^^^^MD^^|||||||||F||||||444-44-4444^HIPPOCRATES^HOWARD H^^^^MD\nOBX|1|SN|1554-5^GLUCOSE^POST 12H CFST:MCNC:PT:SER/PLAS:QN||^182|mg/dl|70_105|H|||F",
  Ccda: '<?xml version="1.0"?><?xml-stylesheet type=\'text/xsl\' href=\'\'?><ClinicalDocument xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns="urn:hl7-org:v3" xmlns:mif="urn:hl7-org:v3/mif" xmlns:voc="urn:hl7-org:v3/voc" xmlns:sdtc="urn:hl7-org:sdtc">    <realmCode code="US"/>    <typeId root="2.16.840.1.113883.1.3" extension="POCD_HD000040"/>    <templateId root="2.16.840.1.113883.10.20.22.1.1"/>    <templateId root="2.16.840.1.113883.10.20.22.1.2"/>    <id root="2.16.840.1.113883.3.109" extension="bf6b3a62-4293-47b4-9f14-c8829a156f4b"/>    <code code="34133-9" displayName="SUMMARIZATION OF EPISODE NOTE"        codeSystem="2.16.840.1.113883.6.1" codeSystemName="LOINC"/>    <title>Continuity of Care Document (C-CDA)</title>    <effectiveTime value="20170528150200-0400"/>    <confidentialityCode code="N" displayName="Normal Sharing" codeSystem="2.16.840.1.113883.5.25"        codeSystemName="HL7 Confidentiality"/>    <languageCode code="en-US"/>    <recordTarget>        <patientRole>            <!-- Here is a public id that has an external meaning based on a root OID that is publically identifiable. -->             <!-- root="1.3.6.1.4.1.41179.2.4" is the assigningAutorityName for                     Direct Trust\'s Patient/Consumer addresses "DT.org PATIENT" -->            <id root="1.3.6.1.4.1.41179.2.4" extension="lisarnelson@direct.myphd.us"                 assigningAutorityName="DT.org PATIENT"/>            <!-- More ids may be used. -->            <!-- Here is the patient\'s MRN at RVHS  -->            <id root="2.16.840.1.113883.1.111.12345" extension="12345-0828"                assigningAuthorityName="River Valley Health Services local patient Medical Record Number" />            <addr>                <streetAddressLine>1 Happy Valley Road</streetAddressLine>                <city>Westerly</city>                <state>RI</state>                <postalCode>02891</postalCode>                <country nullFlavor="UNK"/>            </addr>            <telecom use="WP" value="tel:+1-4013482345"/>            <telecom use="HP" value="tel:+1-4016412345"/>            <telecom value="mailto:lisanelson@gmail.com"/>            <telecom value="mailto:lisarnelson@direct.myphd.us"/>            <patient>                <name use="L">                    <family>Nelson</family>                    <given qualifier="CL">Lisa</given>                </name>                <administrativeGenderCode code="F" displayName="Female"                    codeSystem="2.16.840.1.113883.5.1" codeSystemName="HL7 AdministrativeGender"/>                <birthTime value="19620828"/>                <maritalStatusCode code="M" displayName="Married" codeSystem="2.16.840.1.113883.5.2"                    codeSystemName="HL7 MaritalStatus"/>                <raceCode code="2106-3" displayName="White" codeSystem="2.16.840.1.113883.6.238"                    codeSystemName="CDC Race and Ethnicity"/>                <ethnicGroupCode code="2186-5" displayName="Not Hispanic or Latino"                    codeSystem="2.16.840.1.113883.6.238" codeSystemName="CDC Race and Ethnicity"/>                <languageCommunication>                    <templateId root="2.16.840.1.113883.3.88.11.32.2"/>                    <languageCode code="eng"/>                    <preferenceInd value="true"/>                </languageCommunication>            </patient>            <providerOrganization>                <!-- This is a public id where the root is registered to indicate the National Provider ID -->                <id root="2.16.840.1.113883.4.6" extension="1417947383"                    assigningAuthorityName="National Provider ID"/>                <!-- This is a public id where the root indicates this is a Provider Direct Address. -->                <!-- root="1.3.6.1.4.1.41179.2.1" is the assigningAutorityName for                     Direct Trust\'s Covered Entity addresses "DT.org CE" -->                <id root="1.3.6.1.4.1.41179.2.1" extension="rvhs@rvhs.direct.md" assigningAutorityName="DT.org CE (Covered Entity)"/>                <!-- By including a root OID attribute for a healthcare organization, you can use this information to                 indicate a patient\'s MRN id at that organization.-->                <id root="2.16.840.1.113883.1.111.12345"                     assigningAuthorityName="River Valley Health Services local patient Medical Record Number" />                <name>River Valley Health Services</name>                <telecom use="WP" value="tel:+1-4015394321"/>                <telecom use="WP" value="mailto:rvhs@rvhs.direct.md"/>                <addr>                    <streetAddressLine>823 Main Street</streetAddressLine>                    <city>River Valley</city>                    <state>RI</state>                    <postalCode>028321</postalCode>                    <country>US</country>                </addr>            </providerOrganization>        </patientRole>    </recordTarget>    ...    <component>        <structuredBody>         ...         </structuredBody>    </component></ClinicalDocument>',
  Json: '[{"PatientId": 12434, "MRN": "M0R1N2","FirstName": "Jerry", "LastName": "Smith", "Phone Number": ["1234-5678", "1234-5679"], "Gender": "M", "DOB": "20010110"},{"PatientId": 12465, "MRN": "M0R1N2","FirstName": "Jerry", "LastName": "Smith", "Phone Number": ["1234-5678", "1234-5679"], "Gender": "M", "DOB": "20010110"}]',
};

export const TEMPLATE_COLLECTION_REFERENCE = {
  Hl7v2: "microsofthealth/fhirconverter:default",
  Ccda: "microsofthealth/ccdatemplates:default",
  Json: "microsofthealth/jsontemplates:default",
};

export const ROOT_TEMPLATE = {
  Hl7v2: "ADT_A01",
  Ccda: "CCD",
  Json: "ExamplePatient",
};
